import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Cambio Link por useNavigate
import AdminLayout from '../components/layout/AdminLayout';
import { IArticle } from '../types/Article';
import { getArticles, deleteArticle as apiDeleteArticle, toggleArticleVisibility, toggleArticleFeatured } from '../services/ArticleService';
import ConfirmModal from '../components/common/ConfirmModal';
import { useConfirm } from '../hooks/useConfirm';
import styles from '../styles/Dashboard.module.css'; 
import { PencilSquareIcon, TrashIcon, EyeIcon, EyeSlashIcon, StarIcon } from '@heroicons/react/24/outline';
import { useToast } from '../hooks/useToast';

const ArticlesPage: React.FC = () => {
    const [articles, setArticles] = useState<IArticle[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const toast = useToast();
    const navigate = useNavigate();
    const { isOpen, config, confirm, handleConfirm, handleCancel } = useConfirm();

    const fetchArticles = async () => {
        setLoading(true);
        setError(null);
        try {
            const fetchedArticles = await getArticles();
            setArticles(fetchedArticles);
        } catch (err: any) {
            setError(err.message || "Fallo al cargar artículos.");
            toast.error('Error', 'No se pudieron cargar los artículos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchArticles();
    }, []);

    // --- Lógica de Permisos ---
    const userRole = localStorage.getItem('userRole') || 'observador';
    const canEdit = userRole === 'administrador' || userRole === 'editor';
    const canDelete = userRole === 'administrador';

    const checkPermission = (type: 'edit' | 'delete') => {
        if (type === 'edit' && !canEdit) {
            toast.warning('Acceso Denegado', 'Solo administradores y editores pueden editar.');
            return false;
        }
        if (type === 'delete' && !canDelete) {
            toast.warning('Acceso Denegado', 'Solo administradores pueden eliminar.');
            return false;
        }
        return true;
    };

    // --- Handlers ---
    const handleCreate = (e: React.MouseEvent) => {
        e.preventDefault();
        if (checkPermission('edit')) navigate('/articles/new');
    };

    const handleEditClick = (e: React.MouseEvent, id: number) => {
        e.preventDefault();
        if (checkPermission('edit')) navigate(`/articles/edit/${id}`);
    };

    const handleDelete = async (id: number, title: string) => {
        if (!checkPermission('delete')) return;

        const confirmed = await confirm({
            title: 'Eliminar Artículo',
            message: `¿Estás seguro de eliminar el artículo: "${title}"?`,
            confirmText: 'Eliminar',
            cancelText: 'Cancelar',
            type: 'danger'
        });

        if (confirmed) {
            try {
                const success = await apiDeleteArticle(id);
                if (success) {
                    fetchArticles();
                    toast.success('Éxito', `Artículo "${title}" eliminado.`);
                } else {
                    toast.error('Error', 'Error al eliminar.');
                }
            } catch (err: any) {
                toast.error('Error', `Error: ${err.message}`);
            }
        }
    };

    const handleToggleVisibility = async (id: number, currentState: boolean) => {
        if (!checkPermission('edit')) return;
        try {
            await toggleArticleVisibility(id, !currentState);
            fetchArticles();
            toast.info('Visibilidad actualizada', `Artículo ${!currentState ? 'visible' : 'oculto'}`);
        } catch (err: any) {
            toast.error('Error', `Error: ${err.message}`);
        }
    };

    const handleToggleFeatured = async (id: number, currentState: boolean) => {
        if (!checkPermission('edit')) return;
        try {
            await toggleArticleFeatured(id, !currentState);
            fetchArticles();
            toast.info('Destacado actualizado', `Artículo ${!currentState ? 'destacado' : 'sin destacar'}`);
        } catch (err: any) {
            toast.error('Error', `Error: ${err.message}`);
        }
    };

    if (loading) return <AdminLayout pageTitle="Artículos"><p>Cargando...</p></AdminLayout>;
    if (error) return <AdminLayout pageTitle="Artículos"><p style={{ color: 'red' }}>Error: {error}</p></AdminLayout>;

    return (
        <AdminLayout pageTitle="Gestión de Artículos y Noticias">
            <div className={styles.mainContentHeader}>
                <h2 className={styles.headerTitle}>Artículos Publicados</h2>
                <button onClick={handleCreate} className={styles.primaryButton}>
                    + Nuevo Artículo
                </button>
            </div>

            <div className={styles.recentActivity}>
                <table className={styles.projectTable}>
                    <thead>
                        <tr>
                            <th>Título</th>
                            <th>Categoría</th>
                            <th>Autor</th>
                            <th>Fecha Pub.</th>
                            <th>Destacado</th>
                            <th>Visibilidad</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {articles.map((article) => (
                            <tr key={article.id}>
                                <td>{article.title}</td>
                                <td>{article.category}</td>
                                <td>{article.author}</td>
                                <td>{new Date(article.publishedAt).toLocaleDateString()}</td>
                                <td className={styles.featureCell}>
                                    <button 
                                        onClick={() => handleToggleFeatured(article.id, article.isFeatured)}
                                        className={`${styles.iconButton} ${article.isFeatured ? styles.highlighted : ''}`}
                                    >
                                        <StarIcon className={styles.iconButtonIcon} />
                                    </button>
                                </td>
                                <td className={styles.visibilityCell}>
                                    <button
                                        onClick={() => handleToggleVisibility(article.id, article.isHidden)}
                                        className={`${styles.iconButton} ${article.isHidden ? styles.hidden : ''}`}
                                    >
                                        {article.isHidden ? <EyeSlashIcon className={styles.iconButtonIcon} /> : <EyeIcon className={styles.iconButtonIcon} />}
                                    </button>
                                </td>
                                <td>
                                    <div className={styles.actionsCell}>
                                        <button onClick={(e) => handleEditClick(e, article.id)} className={styles.editButton}>
                                            <PencilSquareIcon className={styles.iconButtonIcon} />
                                        </button>
                                        <button onClick={() => handleDelete(article.id, article.title)} className={styles.deleteButton}>
                                            <TrashIcon className={styles.iconButtonIcon} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {articles.length === 0 && <p className={styles.noProjects}>No hay artículos registrados.</p>}
            </div>
            <ConfirmModal 
                isOpen={isOpen} 
                title={config?.title || ''} 
                message={config?.message || ''} 
                confirmText={config?.confirmText} 
                type={config?.type} 
                onConfirm={handleConfirm} 
                onCancel={handleCancel} 
            />
        </AdminLayout>
    );
};

export default ArticlesPage;